import { tracked } from '@glimmer/tracking';

import { Theme as ThemeEnum } from 'webapp/enums/enums';

/**
 * Tailwind's dark variant. @databox/theme's mode.css resolves its dark token
 * values under `.tw\:dark`, so this class has to track the legacy `theme-*`
 * class or the two token systems would drift apart on theme switch. The colon
 * is part of the class name — Tailwind is configured with prefix(tw).
 */
export const TAILWIND_DARK_CLASS = 'tw:dark';

/**
 * Builds the class string for a locally-themed subtree: the legacy `theme-<id>`
 * class plus `tw:dark` when the theme is dark, so the Tailwind token layer stays
 * in sync with the legacy one (mirrors `updateBodyThemeClass` for the body).
 */
export function themeClassNames(theme: ThemeEnum | string | null | undefined): string {
	if (!theme) {
		return '';
	}
	const classes = [`theme-${theme}`];
	if (theme === ThemeEnum.DARK) {
		classes.push(TAILWIND_DARK_CLASS);
	}
	return classes.join(' ');
}

export default class Theme {
	@tracked id = ThemeEnum.LIGHT;

	constructor(id: ThemeEnum) {
		this.updateBodyThemeClass(id);
	}

	get isDark(): boolean {
		return this.id === ThemeEnum.DARK;
	}

	get defaultClass(): string {
		return `theme-${this.id}`;
	}

	updateBodyThemeClass(themeId: ThemeEnum) {
		this.id = themeId;

		const themeClass = `theme-${themeId}`;

		// cleanup previous theme class, if any
		const bodyClasses = document.body.classList;
		Array.from(bodyClasses).forEach((className) => {
			if (className.startsWith('theme-')) {
				bodyClasses.remove(className);
			}
		});

		// add new theme class
		bodyClasses.add(themeClass);

		// keep the Tailwind token layer on the same theme
		bodyClasses.toggle(TAILWIND_DARK_CLASS, this.isDark);
	}
}
